const authCallbackKeys = ["code", "token_hash", "access_token", "refresh_token", "error", "error_code"] as const;

export function getSupabaseAuthStorageKey(
  supabaseUrl = import.meta.env.VITE_SUPABASE_URL,
): string | null {
  try {
    const projectRef = new URL(supabaseUrl).hostname.split(".", 1)[0];
    return /^[a-z0-9]+$/i.test(projectRef) ? `sb-${projectRef}-auth-token` : null;
  } catch {
    return null;
  }
}

export function hasAuthCallback(
  location: Pick<Location, "search" | "hash"> = window.location,
): boolean {
  const query = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  return authCallbackKeys.some((key) => query.has(key) || hash.has(key))
    || query.get("type") === "recovery"
    || hash.get("type") === "recovery";
}

function isEmbeddedWindow(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Fresh anonymous visitors do not need the full authentication SDK. Persisted
 * sessions, recovery callbacks, and editor/embed sessions still bootstrap
 * immediately so account behavior is unchanged where authentication matters.
 */
export function shouldInitializeAuthImmediately({
  storage = window.localStorage,
  location = window.location,
  embedded = isEmbeddedWindow(),
  supabaseUrl,
}: {
  storage?: Pick<Storage, "getItem">;
  location?: Pick<Location, "search" | "hash">;
  embedded?: boolean;
  supabaseUrl?: string;
} = {}): boolean {
  if (embedded || hasAuthCallback(location)) return true;

  const storageKey = getSupabaseAuthStorageKey(supabaseUrl);
  if (!storageKey) return true;

  try {
    const storedSession = storage.getItem(storageKey);
    return Boolean(storedSession && storedSession !== "null");
  } catch {
    // If storage is unavailable, preserve authentication correctness.
    return true;
  }
}
